import React from "react";
import { makeAction } from "async-action-creator";
import { notification, Button } from "antd";
import * as R from "ramda";
import download from "downloadjs";

import * as gtag from "../../lib/gtag";
import history from "../../history";
import api from "../../utils/api";
import { createApiThunk } from "../../utils/thunk";
import i18n from "../../i18n";
import { FORMS_KEYS } from "../../constants/opportunity";
import { capitalize } from "../../scenes/Opportunity/selectors";
import { transformToApi } from "../../scenes/Opportunity/routes/OpportunityProcess/Step/StepConfiguration/selectors";

export const FETCH_OPPORTUNITY = makeAction("opportunity/FETCH_OPPORTUNITY");
export const NEW_OPPORTUNITY = makeAction("opportunity/NEW_OPPORTUNITY");
export const SAVE_OPPORTUNITY_USERS = makeAction(
  "opportunity/SAVE_OPPORTUNITY_USERS"
);
export const SAVE_OPPORTUNITY_DETAILS = makeAction(
  "opportunity/SAVE_OPPORTUNITY_DETAILS"
);
export const SAVE_OPPORTUNITY_MEDIA = makeAction(
  "opportunity/SAVE_OPPORTUNITY_MEDIA"
);
export const SAVE_OPPORTUNITY_STEPS = makeAction(
  "opportunity/SAVE_OPPORTUNITY_STEPS"
);
export const SAVE_OPPORTUNITY_CANDIDATE = makeAction(
  "opportunity/SAVE_OPPORTUNITY_CANDIDATE"
);
export const SAVE_OPPORTUNITY_QUESTIONS = makeAction(
  "opportunity/SAVE_OPPORTUNITY_QUESTIONS"
);
export const UPLOAD_OPPORTUNITY_DOCUMENT = makeAction(
  "opportunity/UPLOAD_OPPORTUNITY_DOCUMENT"
);
export const MARK_CANDIDATES_AS_READ = makeAction(
  "opportunity/MARK_CANDIDATES_AS_READ"
);
export const EXPORT_OPPORTUNITY_CANDIDATES = makeAction(
  "opportunity/EXPORT_OPPORTUNITY_CANDIDATES"
);
export const UPDATE_CANDIDATE_TAGS = makeAction(
  "opportunity/UPDATE_CANDIDATE_TAGS"
);
export const SAVE_OPPORTUNITY_MULTIPOSTING = makeAction(
  "opportunity/SAVE_OPPORTUNITY_MULTIPOSTING"
);
export const CLOSE_OPPORTUNITY = makeAction("opportunity/CLOSE_OPPORTUNITY");
export const CREATE_OPPORTUNITY = makeAction("opportunity/CREATE_OPPORTUNITY");
export const CREATE_AND_PUBLISH_OPPORTUNITY = makeAction(
  "opportunity/CREATE_AND_PUBLISH_OPPORTUNITY"
);
export const PUBLISH_OPPORTUNITY = makeAction(
  "opportunity/PUBLISH_OPPORTUNITY"
);
export const UNPUBLISH_OPPORTUNITY = makeAction(
  "opportunity/UNPUBLISH_OPPORTUNITY"
);
export const CLONE_OPPORTUNITY = makeAction("opportunity/CLONE_OPPORTUNITY");
export const RESET_OPPORTUNITY_NOTIFICATIONS = makeAction(
  "opportunity/RESET_OPPORTUNITY_NOTIFICATIONS"
);
export const FETCH_OPPORTUNITY_ROOMS = makeAction(
  "opportunity/FETCH_OPPORTUNITY_ROOMS"
);
export const UPDATE_OPPORTUNITY_STEP = makeAction(
  "opportunity/UPDATE_OPPORTUNITY_STEP"
);
export const UPDATE_OPPORTUNITY_STEPS = makeAction(
  "opportunity/UPDATE_OPPORTUNITY_STEPS"
);
export const NEW_CHATBOT_TEST_MESSAGE = makeAction(
  "opportunity/NEW_CHATBOT_TEST_MESSAGE"
);
export const FETCH_CHATBOT_CREATED = makeAction(
  "opportunity/FETCH_CHATBOT_CREATED"
);
export const START_CHATBOT_TEST_MESSAGES = makeAction(
  "opportunity/START_CHATBOT_TEST_MESSAGES"
);
export const SEND_CHATBOT_TEST_MESSAGE = makeAction(
  "opportunity/SEND_CHATBOT_TEST_MESSAGE"
);

export const UPDATE_OPPORTUNITY = makeAction("opportunity/UPDATE_OPPORTUNITY");
export const DESELECT_OPPORTUNITY = makeAction(
  "opportunity/DESELECT_OPPORTUNITY"
);
export const GO_TO_NEXT_WIZARD_STEP = makeAction(
  "opportunity/GO_TO_NEXT_WIZARD_STEP"
);
export const GO_TO_PREVIOUS_WIZARD_STEP = makeAction(
  "opportunity/GO_TO_PREVIOUS_WIZARD_STEP"
);
export const PUBLISH_OPPORTUNITY_FEED = makeAction(
  "opportunity/PUBLISH_OPPORTUNITY_FEED"
);

export const SAVE_MULTIPOSTING_FORM = makeAction(
  "opportunity/SAVE_MULTIPOSTING_FORM"
);
export const SAVE_MULTIPOSTING_UNIVERSITIES_FORM = makeAction(
  "opportunity/SAVE_MULTIPOSTING_UNIVERSITIES_FORM"
);

export const SAVE_SCRAPPER_FORM = makeAction("opportunity/SAVE_SCRAPPER_FORM");

export const SAVE_SHAREDEMAIL_FORM = makeAction(
  "opportunity/SAVE_SHAREDEMAIL_FORM"
);

export const GENERATE_EMAIL_OPPORTUNITY = makeAction(
  "opportunity/GENERATE_EMAIL_OPPORTUNITY"
);
export const SAVE_AUTO_INVITE_EXTERNAL_CANDIDATES = makeAction(
  "opportunity/SAVE_AUTO_INVITE_EXTERNAL_CANDIDATES"
);

export const fetchOpportunity = createApiThunk({
  action: FETCH_OPPORTUNITY,
  request: async (id, { company }) =>
    await api.opportunity.fetch(id, company.id),
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:opportunity.fetchOpportunityRejected")
    })
});

export const saveOpportunityDetails = createApiThunk({
  action: SAVE_OPPORTUNITY_DETAILS,
  request: async ({ id, formData }, { company }) =>
    id ? await api.opportunity.update(id, company.id, formData) : formData,
  onResolved: ({ payload: { id } }) =>
    id &&
    notification.success({
      message: i18n.t("global:good"),
      description: i18n.t("toasts:opportunity.saveOpportunityDetailsResolved")
    }),
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:opportunity.saveOpportunityDetailsRejected")
    })
});

export const saveOpportunityUsers = createApiThunk({
  action: SAVE_OPPORTUNITY_USERS,
  request: async ({ id, users }, { company }) =>
    api.opportunity.update(id, company.id, { guest: users }),
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:opportunity.updateOpportunityUsersError")
    })
});

export const saveOpportunityCandidate = createApiThunk({
  action: SAVE_OPPORTUNITY_CANDIDATE,
  request: async ({ id, formData }, { company }) =>
    id ? await api.opportunity.update(id, company.id, formData) : formData,
  onResolved: ({ payload: { id } }) =>
    id &&
    notification.success({
      message: i18n.t("global:good"),
      description: i18n.t("toasts:opportunity.saveOpportunityCandidateResolved")
    }),
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:opportunity.saveOpportunityCandidateRejected")
    })
});

export const saveOpportunityQuestions = createApiThunk({
  action: SAVE_OPPORTUNITY_QUESTIONS,
  request: async ({ id, formData }, { company }) =>
    id ? await api.opportunity.update(id, company.id, formData) : formData,
  onResolved: ({ payload: { id } }) =>
    id &&
    notification.success({
      message: i18n.t("global:good"),
      description: i18n.t("toasts:opportunity.saveOpportunityQuestionsResolved")
    }),
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:opportunity.saveOpportunityQuestionsRejected")
    })
});

export const uploadOpportunityDocument = createApiThunk({
  action: UPLOAD_OPPORTUNITY_DOCUMENT,
  request: async ({ id, file }, { company }) =>
    await api.opportunity.uploadDocument(id, company.id, file),
  onResolved: ({ payload: { id } }) =>
    id &&
    notification.success({
      message: i18n.t("global:good"),
      description: i18n.t(
        "toasts:opportunity.uploadOpportunityDocumentResolved"
      )
    }),
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t(
        "toasts:opportunity.uploadOpportunityDocumentRejected"
      )
    })
});

export const markCandidatesAsRead = createApiThunk({
  action: MARK_CANDIDATES_AS_READ,
  request: async ({ id, candidates, read }, { company }) =>
    await api.opportunity.markCandidatesAsRead(id, company.id, candidates, read)
});

export const exportOpportunityCandidates = createApiThunk({
  action: EXPORT_OPPORTUNITY_CANDIDATES,
  request: async (id, { company }) => {
    const data = await api.opportunity.exportCandidates(id, company.id);
    return download(data, "candidates.csv", "text/plain");
  }
});

export const updateCandidateTags = createApiThunk({
  action: UPDATE_CANDIDATE_TAGS,
  request: async (
    { opportunityId, candidates, addTags, removeTags },
    { company }
  ) =>
    await api.opportunity.updateCandidateTags(
      opportunityId,
      company.id,
      candidates,
      addTags,
      removeTags
    )
});

export const saveOpportunityMultipostingUniversity = createApiThunk({
  action: SAVE_MULTIPOSTING_UNIVERSITIES_FORM,
  request: async ({ publishedOnCompanies, id }, { company }) =>
    api.opportunity.update(id, company.id, { publishedOnCompanies }),
  onResolved: () =>
    notification.success({
      message: i18n.t("global:good"),
      description: i18n.t(
        "toasts:opportunity.saveOpportunityMultipostingUniversitiesResolved"
      )
    }),
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t(
        "toasts:opportunity.saveOpportunityMultipostingUniversitiesRejected"
      )
    })
});

export const saveOpportunityMedia = createApiThunk({
  action: SAVE_OPPORTUNITY_MEDIA,
  request: async ({ id, formData }, { company }) => {
    if (
      R.pipe(
        R.values,
        R.all(R.isNil)
      )(formData)
    ) {
      return;
    }

    return id
      ? await api.opportunity.update(id, company.id, formData)
      : formData;
  },
  onResolved: ({ payload: { id } }) =>
    id &&
    notification.success({
      message: i18n.t("global:good"),
      description: i18n.t("toasts:opportunity.saveOpportunityMediaResolved")
    }),
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:opportunity.saveOpportunityMediaRejected")
    })
});

export const saveOpportunitySteps = createApiThunk({
  action: SAVE_OPPORTUNITY_STEPS,
  request: async ({ formData }, { user, company }) => {
    const excludedUsers = R.pipe(
      R.filter(
        R.pipe(
          R.propOr([], "roles"),
          R.either(R.includes("admin"), R.includes("headadmin"))
        )
      ),
      R.pluck("id"),
      R.append(user.id)
    )(company.users);

    const interviewersNotInvited = R.pipe(
      R.chain(R.pathOr([], ["config", "users"])),
      R.uniqBy(R.prop("id")),
      R.reject(R.propSatisfies(id => R.includes(id, excludedUsers), "id")),
      R.map(R.assoc("permissions", ["chat"]))
    )(formData.steps);

    return {
      guest: interviewersNotInvited,
      steps: R.map(R.omit(["id"]))(formData.steps)
    };
  },
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:opportunity.saveOpportunityStepsRejected")
    })
});

export const saveOpportunityMultiposting = createApiThunk({
  action: SAVE_OPPORTUNITY_MULTIPOSTING,
  request: async ({ id, formData }, { company }) =>
    id ? await api.opportunity.update(id, company.id, formData) : formData,
  onResolved: ({ payload: { id } }) =>
    id &&
    notification.success({
      message: i18n.t("global:good"),
      description: i18n.t(
        "toasts:opportunity.saveOpportunityMultipostingResolved"
      )
    }),
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t(
        "toasts:opportunity.saveOpportunityMultipostingRejected"
      )
    })
});

export const publishOpportunityFeed = createApiThunk({
  action: PUBLISH_OPPORTUNITY_FEED,
  request: async (feed, { company, opportunity }) => {
    const data = await api.opportunity.publish(
      opportunity.selected.id,
      company.id,
      feed
    );
    return { ...data, feed };
  },
  onResolved: ({ response }) =>
    notification.success({
      message: i18n.t("global:good"),
      description: i18n.t("toasts:opportunity.publishOpportunityFeedResolved", {
        feed: capitalize(response.feed)
      })
    }),
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:opportunity.publishOpportunityFeedRejected")
    })
});

export const saveOpportunityAutoInviteExternalCandidates = createApiThunk({
  action: SAVE_AUTO_INVITE_EXTERNAL_CANDIDATES,
  request: async (autoInviteExternalCandidates, { company, opportunity }) => {
    const data = await api.opportunity.update(
      opportunity.selected.id,
      company.id,
      {
        autoInviteExternalCandidates
      }
    );
    return data;
  },
  onResolved: ({ response: { id, autoInviteExternalCandidates } }) =>
    id &&
    notification.success({
      message: i18n.t("global:good"),
      description: autoInviteExternalCandidates
        ? i18n.t(
            "toasts:opportunity.saveAutoInviteExternalCandidatesTrueResolved"
          )
        : i18n.t(
            "toasts:opportunity.saveAutoInviteExternalCandidatesFalseResolved"
          )
    }),
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t(
        "toasts:opportunity.saveAutoInviteExternalCandidatesRejected"
      )
    })
});

const createOpportunityRequest = publish => async (
  _,
  { company, opportunity: { selected } },
  dispatch
) => {
  const newOpportunity = await api.opportunity.create(
    R.pipe(
      R.omit(["steps"]),
      R.assoc("status", publish ? "published" : "open")
    )(selected),
    company.id
  );

  gtag.event({
    category: "Opportunities",
    action: publish ? "Create and publish opportunity" : "Create opportunity"
  });

  if (selected.steps.length > 0) {
    const steps = selected.steps;
    dispatch(
      updateOpportunitySteps(
        {
          opportunityId: newOpportunity.id,
          steps
        },
        { showNotification: false }
      )
    );
  }

  localStorage.removeItem("opportunity");
  history.push(`/oportunidades/${newOpportunity.id}`);

  return newOpportunity;
};

export const createOpportunity = createApiThunk({
  action: CREATE_OPPORTUNITY,
  request: createOpportunityRequest(false),
  onResolved: () =>
    notification.success({
      message: i18n.t("global:good"),
      description: i18n.t(`toasts:opportunity.createOpportunityResolved`)
    }),
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:opportunity.createOpportunityRejected")
    })
});

export const createAndPublishOpportunity = createApiThunk({
  action: CREATE_AND_PUBLISH_OPPORTUNITY,
  request: createOpportunityRequest(true),
  onResolved: () =>
    notification.success({
      message: i18n.t("global:good"),
      description: i18n.t("toasts:opportunity.publishOpportunityResolved")
    }),
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:opportunity.publishOpportunityRejected")
    })
});

export const publishOpportunity = createApiThunk({
  action: PUBLISH_OPPORTUNITY,
  request: async (id, { company }) =>
    await api.opportunity.update(id, company.id, {
      status: "published",
      finished: false
    }),
  onResolved: () =>
    notification.success({
      message: i18n.t("global:good"),
      description: i18n.t("toasts:opportunity.publishOpportunityResolved")
    }),
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:opportunity.publishOpportunityRejected")
    })
});

export const unpublishOpportunity = createApiThunk({
  action: UNPUBLISH_OPPORTUNITY,
  request: async ({ id, closingReason, closingReasonOther }, { company }) =>
    await api.opportunity.update(id, company.id, {
      status: "open",
      finished: true,
      closingReason,
      ...(closingReasonOther ? { closingReasonOther } : {})
    }),
  onResolved: () =>
    notification.success({
      message: i18n.t("global:good"),
      description: i18n.t("toasts:opportunity.unpublishOpportunityResolved")
    }),
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:opportunity.unpublishOpportunityRejected")
    })
});

export const closeOpportunity = createApiThunk({
  action: CLOSE_OPPORTUNITY,
  request: async (id, { company }) => {
    const opportunity = await api.opportunity.update(id, company.id, {
      status: "closed"
    });

    history.push("/oportunidades");

    return opportunity;
  },
  onResolved: () =>
    notification.success({
      message: i18n.t("global:good"),
      description: i18n.t("toasts:opportunity.closeOpportunityResolved")
    }),
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:opportunity.closeOpportunityRejected")
    })
});

export const cloneOpportunity = createApiThunk({
  action: CLONE_OPPORTUNITY,
  request: async (sourceOpportunity, { company }) => {
    const opportunityKeys = R.pipe(
      R.values,
      R.flatten,
      R.reject(R.equals("steps"))
    )(FORMS_KEYS);

    const opportunity = await api.opportunity.create(
      {
        ...R.pick(opportunityKeys, sourceOpportunity),
        id: undefined,
        createdAt: undefined,
        updatedAt: undefined,
        status: "open",
        finished: undefined,
        title: sourceOpportunity.title + " (copia)",
        // Remove ids from screening questions
        questionsAdvanced: R.pipe(
          R.propOr([], "questionsAdvanced"),
          R.map(R.omit(["id", "idChatbot"]))
        )(sourceOpportunity)
      },
      company.id
    );

    const sourceSteps = await api.opportunity.process.fetch(
      company.id,
      sourceOpportunity.id
    );

    const customSteps = R.reject(R.propEq("default", true), sourceSteps);

    const steps = await Promise.all(
      R.map(
        step =>
          api.opportunity.process.addStep(
            company.id,
            opportunity.id,
            R.omit(
              ["id", "_id", "default", "idOpportunity", "columnOrder"],
              step
            )
          ),
        customSteps
      )
    );

    return {
      ...opportunity,
      steps
    };
  },
  onResolved: ({ response: opportunity }) => {
    const key = `open${Date.now()}`;
    const btn = (
      <Button
        type="primary"
        size="small"
        onClick={() => {
          history.push(`/oportunidades/${opportunity.id}`);
          notification.close(key);
        }}
      >
        {i18n.t("toasts:opportunity.cloneOpportunityButton")}
      </Button>
    );

    return notification.success({
      message: i18n.t("global:good"),
      description: i18n.t("toasts:opportunity.cloneOpportunityResolved"),
      key,
      btn,
      duration: 6
    });
  },
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:opportunity.cloneOpportunityRejected")
    })
});

export const updateOpportunitySteps = createApiThunk({
  action: UPDATE_OPPORTUNITY_STEPS,
  request: async ({ opportunityId, steps }, { company }, dispatch) => {
    const resolvedSteps = await api.opportunity.process.updateSteps(
      company.id,
      opportunityId,
      R.map(step => transformToApi(step, ["rooms"]))(steps)
    );

    // When creating a new step that has rooms, we need to first
    // create the step, get the ID of the step and update it with
    // the rooms after creation.

    const stepsToUpdateWithRooms = steps.filter(
      ({ unsaved, rooms }) => unsaved && rooms && rooms.length > 0
    );

    if (stepsToUpdateWithRooms && stepsToUpdateWithRooms.length > 0) {
      await Promise.all(
        // Here we merge the saved step (in resolvedSteps - identified with `order`) with the
        // local one that has the rooms in it and executing updateOpportunityStep
        // to create the rooms for that step.

        stepsToUpdateWithRooms.map(localStep => {
          const savedStep = R.pipe(
            R.filter(R.propEq("type", "add-step")),
            R.find(R.pathEq(["step", "order"], localStep.order)),
            R.prop("step")
          )(resolvedSteps);

          if (savedStep) {
            return dispatch(
              updateOpportunityStep(
                {
                  opportunityId: savedStep.idOpportunity,
                  stepId: savedStep.id,
                  formData: R.pipe(
                    R.assoc("rooms", localStep.rooms),
                    R.omit(["_id", "id", "idOpportunity", "default"])
                  )(savedStep)
                },
                { showNotification: false }
              )
            );
          }

          return null;
        })
      );
    }

    return resolvedSteps;
  },
  onResolved: ({ params: { showNotification = true } }) =>
    showNotification &&
    notification.success({
      message: i18n.t("global:good"),
      description: i18n.t("toasts:opportunity.updateOpportunityStepsResolved")
    }),
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:opportunity.updateOpportunityRejected")
    })
});

export const createOpportunityConfigPayload = (
  formData,
  selectedOpportunity
) => ({
  config: R.mergeRight(
    R.pathOr({}, ["config"], selectedOpportunity),
    R.omit(["autoMatchOnRequirements"], formData)
  ),
  showJobType: selectedOpportunity.showJobType,
  showContractType: selectedOpportunity.showContractType,
  ...(R.has("autoMatchOnRequirements", formData)
    ? { autoMatchOnRequirements: Boolean(formData.autoMatchOnRequirements) }
    : {})
});

export const updateOpportunity = (opportunityId, companyId, formData) => async (
  dispatch,
  getState
) => {
  try {
    dispatch(UPDATE_OPPORTUNITY.start());
    const selectedOpportunity = R.pathOr(
      {},
      ["opportunity", "selected"],
      getState()
    );
    const opportunityData = await api.opportunity.update(
      opportunityId,
      companyId,
      createOpportunityConfigPayload(formData, selectedOpportunity)
    );

    dispatch(UPDATE_OPPORTUNITY.resolve(opportunityData));
    notification.success({
      message: i18n.t("global:good"),
      description: i18n.t(`toasts:opportunity.updateOpportunityResolved`)
    });
  } catch (error) {
    dispatch(UPDATE_OPPORTUNITY.reject({ opportunityId, error }));
    notification.error({
      message: "Error",
      description: i18n.t(`toasts:opportunity.updateOpportunityRejected`)
    });
  }
};

export const fetchOpportunityRooms = createApiThunk({
  action: FETCH_OPPORTUNITY_ROOMS,
  request: async ({ opportunityId, step }, { company }) => {
    // Es necesario fetchear esto porque desde backend no nos envían los datos de las rooms
    if (R.propEq("type", "group-interview", step)) {
      const rooms = await api.opportunity.process.step.room.list(
        company.id,
        opportunityId,
        step.id
      );

      return R.assoc("rooms", rooms, step);
    }

    return R.assoc("rooms", [], step);
  },
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:opportunity.fetchOpportunityStepRejected")
    })
});

export const updateOpportunityStep = createApiThunk({
  action: UPDATE_OPPORTUNITY_STEP,
  request: async (
    { opportunityId, stepId, formData },
    { company, user, opportunity }
  ) => {
    const rooms = R.pipe(
      R.propOr([], "rooms"),
      R.map(
        R.cond([
          [
            R.has("isNew"),
            room =>
              api.opportunity.process.step.room.create(
                company.id,
                opportunityId,
                stepId,
                R.omit(
                  ["id", "candidates", "isNew", "isEdited", "isDeleted"],
                  room
                )
              )
          ],
          [
            R.has("isEdited"),
            room =>
              api.opportunity.process.step.room.update(
                company.id,
                opportunityId,
                stepId,
                R.omit(["isNew", "isEdited", "isDeleted"], room)
              )
          ],
          [
            R.has("isDeleted"),
            room =>
              api.opportunity.process.step.room.delete(
                company.id,
                opportunityId,
                stepId,
                room.id
              )
          ],
          [R.T, R.always(null)]
        ])
      ),
      R.reject(R.isNil)
    )(formData);

    const interviewers = R.pipe(R.pathOr([], ["config", "users"]))(formData);

    const excludedUsers = R.pipe(
      R.filter(
        R.pipe(
          R.propOr([], "roles"),
          R.either(R.includes("admin"), R.includes("headadmin"))
        )
      ),
      R.pluck("id"),
      R.append(user.id)
    )(company.users);

    const usersToInvite = R.reject(
      R.propSatisfies(id => R.includes(id, excludedUsers))
    )(interviewers);

    const guest = R.pipe(
      R.pathOr([], ["selected", "guest"]),
      // Add "chat" permission to users that are already guests of the opportunity
      R.map(
        R.when(
          R.propSatisfies(id => !R.includes(id, excludedUsers), "id"),
          R.evolve({
            permissions: R.pipe(
              R.defaultTo([]),
              R.append("chat"),
              R.uniq // Only add "chat" permissions one time
            )
          })
        )
      ),
      // Add new guests
      guests =>
        R.concat(
          guests,
          R.map(R.assoc("permissions", ["chat"]), usersToInvite)
        ),
      // Remove duplicated users
      R.uniqBy(R.prop("id"))
    )(opportunity);

    const updateProcess = await Promise.all([
      api.opportunity.update(opportunityId, company.id, { guest }),
      api.opportunity.process.step.update(
        company.id,
        opportunityId,
        stepId,
        R.omit(["rooms"])(formData)
      )
    ]);

    await Promise.all(rooms);

    return updateProcess[1];
  },
  onResolved: ({ params: { showNotification = true } }) =>
    showNotification &&
    notification.success({
      message: i18n.t("global:good"),
      description: i18n.t("toasts:opportunity:updateOpportunityStepResolved")
    }),
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:opportunity.updateOpportunityStepRejected")
    })
});

export const startChatbotTestMessages = createApiThunk({
  action: START_CHATBOT_TEST_MESSAGES,
  request: async (_, { user, company, opportunity: { selected } }) => {
    const idChatbot = `${company.id}_${selected.id}_${selected.selectedStep.id}`;

    return api.chatbot.message.init({
      idChatbot,
      idUser: R.prop("id", user)
    });
  }
});

export const fetchChatbotCreated = createApiThunk({
  action: FETCH_CHATBOT_CREATED,
  request: async (_, { company, opportunity: { selected } }) => {
    const idChatbot = `${company.id}_${selected.id}_${selected.selectedStep.id}`;
    const result = await api.chatbot.isCreated(idChatbot);
    const isCreated = !!R.prop("chatbot_id", result);
    return isCreated;
  }
});

export const sendChatbotTestMessage = createApiThunk({
  action: SEND_CHATBOT_TEST_MESSAGE,
  request: async (message, { user, company, opportunity: { selected } }) => {
    const idChatbot = `${company.id}_${selected.id}_${selected.selectedStep.id}`;

    return api.chatbot.message.send({
      message,
      idChatbot,
      idUser: R.prop("id", user)
    });
  }
});

export const resetOpportunityNotifications = createApiThunk({
  action: RESET_OPPORTUNITY_NOTIFICATIONS,
  request: async ({ id, type }, { company }) =>
    await api.opportunities.resetNotifications(id, company.id, type)
});

export const generateEmailOpportunity = createApiThunk({
  action: GENERATE_EMAIL_OPPORTUNITY,
  request: async (id, { company }) => {
    const opportunity = await api.opportunity.update(id, company.id, {
      createJobPostEmail: true
    });
    const jobPostEmail = R.propOr(null, "jobPostEmail", opportunity);
    const emailError = R.propOr(null, "emailError", opportunity);
    return { jobPostEmail, emailError };
  },
  onResolved: ({ response }) => {
    if (!response.emailError) {
      return notification.success({
        message: i18n.t("global:good"),
        description: i18n.t("toasts:opportunity.saveGeneratedEmailResolved")
      });
    } else {
      return notification.error({
        message: i18n.t("global:error"),
        description: i18n.t("toasts:opportunity.saveGeneratedEmailRejected")
      });
    }
  },
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:opportunity.saveGeneratedEmailRejected")
    })
});
